import type { EmployeeDirectoryEntry } from '@shared/types';

export type OrgPerson = Pick<
  EmployeeDirectoryEntry,
  'id' | 'name' | 'title' | 'department' | 'employmentStatus' | 'managerId' | 'avatarStorageKey'
>;

export const UNASSIGNED_NODE_ID = '__unassigned__';

export interface EmployeeOrgNode {
  kind: 'employee';
  id: string;
  person: OrgPerson;
  children: OrgChartNode[];
}

export interface UnassignedOrgNode {
  kind: 'unassigned';
  id: typeof UNASSIGNED_NODE_ID;
  children: OrgChartNode[];
}

export type OrgChartNode = EmployeeOrgNode | UnassignedOrgNode;

function compareEmployeeNodes(a: EmployeeOrgNode, b: EmployeeOrgNode): number {
  return a.person.name.localeCompare(b.person.name) || a.person.id.localeCompare(b.person.id);
}

/** Build a forest from managerId links; break cycles; treat orphans as roots. */
export function buildOrgForest(people: OrgPerson[]): EmployeeOrgNode[] {
  const ids = new Set(people.map((person) => person.id));
  const nodes = new Map<string, EmployeeOrgNode>(
    people.map((person) => [person.id, {
      kind: 'employee',
      id: person.id,
      person,
      children: [],
    }]),
  );

  const parentOf = new Map<string, string | null>();
  for (const person of people) {
    if (
      person.managerId
      && ids.has(person.managerId)
      && person.managerId !== person.id
    ) {
      parentOf.set(person.id, person.managerId);
    } else {
      parentOf.set(person.id, null);
    }
  }

  for (const id of ids) {
    const seen: string[] = [];
    let current: string | null | undefined = id;
    while (current) {
      if (seen.includes(current)) {
        parentOf.set(current, null);
        break;
      }
      seen.push(current);
      current = parentOf.get(current) ?? null;
    }
  }

  const roots: EmployeeOrgNode[] = [];
  for (const person of people) {
    const node = nodes.get(person.id)!;
    const parentId = parentOf.get(person.id);
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortTree = (node: EmployeeOrgNode) => {
    node.children = [...node.children];
    // oxlint-disable-next-line unicorn/no-array-sort -- ES2022 target has no Array#toSorted.
    node.children.sort((a, b) => {
      if (a.kind !== 'employee' || b.kind !== 'employee') return 0;
      return compareEmployeeNodes(a, b);
    });
    for (const child of node.children) {
      if (child.kind === 'employee') sortTree(child);
    }
  };

  const sortedRoots = [...roots];
  // oxlint-disable-next-line unicorn/no-array-sort -- ES2022 target has no Array#toSorted.
  sortedRoots.sort(compareEmployeeNodes);
  for (const root of sortedRoots) sortTree(root);
  return sortedRoots;
}

export function countEmployeeNodes(node: OrgChartNode): number {
  return (node.kind === 'employee' ? 1 : 0)
    + node.children.reduce((total, child) => total + countEmployeeNodes(child), 0);
}

/** Normalize the forest to one inferred head and an optional Unassigned branch. */
export function buildOrgChart(people: OrgPerson[]): EmployeeOrgNode | null {
  const roots = buildOrgForest(people);
  if (roots.length === 0) return null;

  const rankedRoots = [...roots];
  // oxlint-disable-next-line unicorn/no-array-sort -- ES2022 target has no Array#toSorted.
  rankedRoots.sort((a, b) =>
    countEmployeeNodes(b) - countEmployeeNodes(a)
    || compareEmployeeNodes(a, b));
  const head = rankedRoots[0]!;
  const remainingRoots = rankedRoots.slice(1);
  // oxlint-disable-next-line unicorn/no-array-sort -- ES2022 target has no Array#toSorted.
  remainingRoots.sort(compareEmployeeNodes);

  if (remainingRoots.length > 0) {
    head.children = [
      ...head.children,
      {
        kind: 'unassigned',
        id: UNASSIGNED_NODE_ID,
        children: remainingRoots,
      },
    ];
  }

  return head;
}

export function collectExpandableIds(root: OrgChartNode): string[] {
  const ids: string[] = [];
  const walk = (node: OrgChartNode) => {
    if (node.children.length === 0) return;
    ids.push(node.id);
    node.children.forEach(walk);
  };
  walk(root);
  return ids;
}

export function defaultExpandedIds(root: OrgChartNode, maxDepth = 2): Set<string> {
  const ids = new Set<string>();
  const walk = (node: OrgChartNode, depth: number) => {
    if (node.children.length === 0) return;
    if (depth < maxDepth) {
      ids.add(node.id);
      node.children.forEach((child) => walk(child, depth + 1));
    }
  };
  walk(root, 0);
  return ids;
}
